import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";
import { Section, Field, RetryImage } from "./ui";
import ShareButtons from "../../components/ShareButtons";
import { SiteVisitStep } from "./SiteVisitStep";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function BookStep(props) {
  // Autumn offer: while site-visit mode is on there is no advance booking fee —
  // the client books a free 1-hour site visit instead of paying £499.
  if (props.settings?.site_visit_mode) return <SiteVisitStep {...props} />;
  return <PayAndBookStep {...props} />;
}

function PayAndBookStep({ config, site, route, planningRef, postcode, quoteResult, settings, planningQuoteId, planningContact, pdQuote, contact, propType, pdDepth, onBack }) {
  const [details, setDetails] = useState({ name: planningContact?.name || contact?.name || "", email: planningContact?.email || contact?.email || "", phone: planningContact?.mobile || contact?.phone || "", address:"", comments:"" });
  const [dates, setDates] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [express, setExpress] = useState(false);
  const [policyAck, setPolicyAck] = useState(false);
  const [expressEarliest, setExpressEarliest] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState(null);
  const [codeChecking, setCodeChecking] = useState(false);
  useEffect(() => {
    if (route === "planning") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/quote/share", { data: { config, site, route, propType, pdDepth } });
        if (!cancelled) {
          setShareUrl(`${window.location.origin}/api/s/${r.data.id}`);
          localStorage.setItem("de_resume", JSON.stringify({ id: r.data.id, total: quoteResult?.total_inc_vat || 0, ts: Date.now() }));
        }
      } catch (e) { console.debug("Share/resume link unavailable (non-blocking):", e); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only: creates the resume/share link once
  const isPlanning = route === "planning";

  useEffect(() => {
    setStartDate("");
    const q = isPlanning ? "?route=planning" : (express ? "?express=true" : "");
    api.get(`/quote/availability${q}`).then(r=>setDates(r.data.available));
  }, [express, isPlanning]);

  useEffect(() => {
    if (isPlanning) return;
    api.get("/quote/availability?express=true").then(r=>setExpressEarliest(r.data.available?.[0]||null));
  }, [isPlanning]);

  const expressFee = settings?.express_fee || 2499;
  const bookingFeeCap = settings?.booking_fee ?? 499;
  const baseForCode = isPlanning
    ? (quoteResult?.total_inc_vat || 0)
    : Math.max(0, (quoteResult?.total_before_discount ?? quoteResult?.total_inc_vat ?? 0) - (quoteResult?.door_offer_amount || 0));
  const codeAmount = appliedCode
    ? (appliedCode.type === "pct" ? baseForCode * appliedCode.value / 100 : Math.min(appliedCode.value, baseForCode))
    : 0;
  const effectiveTotal = appliedCode ? Math.max(0, baseForCode - codeAmount) : (quoteResult?.total_inc_vat || 0);
  const fullDeposit = useMemo(() => Math.max(effectiveTotal * (settings?.deposit_pct || 0.10), bookingFeeCap), [effectiveTotal, settings, bookingFeeCap]);
  const bookingFee = Math.min(bookingFeeCap, fullDeposit);
  const depositBalance = Math.max(0, fullDeposit - bookingFee);
  const depositTotal = bookingFee + (express && !isPlanning ? expressFee : 0);

  const applyCode = async () => {
    const code = codeInput.trim();
    if (!code) return;
    setCodeChecking(true);
    try {
      const r = await api.post("/discount/validate", { code });
      if (r.data.valid) { setAppliedCode(r.data); toast.success(`Code ${r.data.code} applied — ${r.data.label}`); }
      else { setAppliedCode(null); toast.error(r.data.error || "Invalid code"); }
    } catch { toast.error("Could not check that code"); }
    setCodeChecking(false);
  };
  const removeCode = () => { setAppliedCode(null); setCodeInput(""); };

  const submit = async () => {
    if (!policyAck) { toast.error("Please tick the deposit & cancellation acknowledgement to continue."); return; }
    if (!startDate) { toast.error("Pick a start date."); return; }
    if (!details.email || !details.name) { toast.error("Name and email are required."); return; }
    if (!details.phone || !details.phone.trim()) { toast.error("A phone number is required to book."); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...details,
        postcode, route, planning_ref: planningRef,
        quote: isPlanning ? { route: "planning" } : { ...config, site, route },
        planning_quote_id: isPlanning ? planningQuoteId : undefined,
        quote_id: isPlanning ? planningQuoteId : (pdQuote?.quote_id || undefined),
        start_date: startDate,
        cooling_off_consent: true,
        express,
        payment_method: "card",
        discount_code: appliedCode?.code || undefined,
      };
      const r = await api.post("/booking/create", payload);
      const co = await api.post("/payments/checkout", { reference: r.data.reference, origin: window.location.origin });
      window.location.href = co.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create booking");
      setSubmitting(false);
    }
  };

  return (
    <Section title="Pick a start date &amp; pay your £499 booking fee" eyebrow="Final step · Book">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <Field label="Full name" v={details.name} on={v=>setDetails({...details,name:v})} testid="bk-name"/>
          <Field label="Email" type="email" v={details.email} on={v=>setDetails({...details,email:v})} testid="bk-email"/>
          <Field label="Phone *" v={details.phone} on={v=>setDetails({...details,phone:v})} testid="bk-phone"/>
          <Field label="Site address" v={details.address} on={v=>setDetails({...details,address:v})} testid="bk-address"/>
          <div>
            <label className="label-eyebrow block mb-2 mt-4">Anything else we should know?</label>
            <textarea data-testid="bk-comments" rows={4} value={details.comments} onChange={e=>setDetails({...details,comments:e.target.value})} className="w-full border border-[#E5E7EB] bg-white px-4 py-3"/>
          </div>
          {!isPlanning && (
            <div className="de-card p-5 mt-6" data-testid="bk-share">
              <div className="font-bold mb-1 text-sm">Love your design? Share it</div>
              <p className="text-xs text-[#6B7280] leading-relaxed mb-3">Send your exact spec to a partner, or open it later to pick up right where you left off.</p>
              {shareUrl
                ? <ShareButtons url={shareUrl} text="Check out the home extension I designed with The Extension Works:" testidPrefix="bk-share" />
                : <div className="text-xs text-[#6B7280]">Preparing your share link…</div>}
            </div>
          )}
        </div>
        <div>
          {!isPlanning && (
          <div className="border border-[#C79A3B] bg-white p-4 mb-4">
            <label className="flex items-start gap-3 text-sm">
              <input data-testid="bk-express" type="checkbox" checked={express} onChange={e=>setExpress(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0 accent-[#C79A3B]"/>
              <span>
                <b>Request express service (+£{expressFee.toLocaleString("en-GB")})</b>{expressEarliest && <span className="text-[#C79A3B] font-bold"> · start as soon as {new Date(expressEarliest).toLocaleDateString("en-GB",{day:"numeric",month:"long"})}</span>}<br/>
                <span className="text-[#6B7280] text-xs">Overrides the standard 4-week lead time — opens start dates from just 5 working days away. The full deposit + express fee is <b>non-refundable</b> once paid.</span>
              </span>
            </label>
          </div>
          )}
          {isPlanning && (
            <div className="border border-[#E5E7EB] bg-[#F4F4F4] p-4 mb-4 text-sm text-[#6B7280]" data-testid="bk-planning-note">
              Planning-permission builds are scheduled to allow for plan production and Building Control sign-off — we'll confirm your exact start date after survey.
            </div>
          )}

          {!isPlanning && pdQuote && (
            <div className="border border-[#E5E7EB] bg-white p-4 mb-6" data-testid="pd-quote-ref">
              <div className="flex flex-wrap sm:flex-nowrap items-start gap-3">
                <div className="flex gap-3 shrink-0">
                  <img src={`${BACKEND}${pdQuote.photo_url}`} alt="Your extension exterior" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} className="w-24 h-20 object-cover border border-[#E5E7EB] bg-[#F4F4F4]" />
                  {pdQuote.interior_photo_url && (
                    <RetryImage src={`${BACKEND}${pdQuote.interior_photo_url}`} alt="Your extension interior" className="w-24 h-20 object-cover border border-[#E5E7EB] bg-[#F4F4F4]" testid="pd-quote-interior" />
                  )}
                </div>
                <div className="text-sm min-w-0">
                  <div className="label-eyebrow">Your quote reference</div>
                  <div className="text-xl font-black text-[#C79A3B] break-all" data-testid="pd-quote-reference">{pdQuote.reference}</div>
                  <div className="text-xs text-[#6B7280] mt-1">Quote valid until <b>{new Date(pdQuote.valid_until).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</b>. Quote us this reference if you call.</div>
                </div>
              </div>
            </div>
          )}

          <div className="label-eyebrow mb-2">Available start dates</div>
          <div className="border border-[#E5E7EB] p-3 sm:p-4 max-h-[280px] overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2 bg-white">
            {dates.length===0 && <div className="col-span-full text-sm text-[#6B7280]">Loading available dates…</div>}
            {dates.map(d=>(
              <button key={d} data-testid={`date-${d}`} onClick={()=>setStartDate(d)} className={`px-1 py-2.5 text-xs border whitespace-nowrap ${startDate===d?"bg-[#C79A3B] text-white border-[#C79A3B]":"border-[#E5E7EB] hover:border-[#12263A]"}`}>
                {new Date(d).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"2-digit"})}
              </button>
            ))}
          </div>

          <div className="border border-[#E5E7EB] bg-white p-4 mt-6" data-testid="bk-discount">
            <label className="label-eyebrow block mb-2">Discount code</label>
            {appliedCode ? (
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  <span className="font-bold text-[#1F6B3B]" data-testid="bk-discount-applied">{appliedCode.code}</span>
                  <span className="text-[#6B7280]"> · {appliedCode.label} (−£{Math.round(codeAmount).toLocaleString("en-GB")})</span>
                  {appliedCode.terms && <p className="text-[11px] text-[#9CA3AF] mt-1 leading-relaxed">{appliedCode.terms}</p>}
                </div>
                <button data-testid="bk-discount-remove" onClick={removeCode} className="text-xs font-bold uppercase tracking-widest text-[#6B7280] hover:text-[#C2401F] shrink-0">Remove</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input data-testid="bk-discount-input" value={codeInput} onChange={e => setCodeInput(e.target.value.toUpperCase())}
                  placeholder="Enter code" className="border border-[#E5E7EB] px-3 py-2 text-sm flex-1 min-w-0 uppercase" />
                <button data-testid="bk-discount-apply" onClick={applyCode} disabled={codeChecking} className="de-btn-secondary text-sm !px-4 shrink-0 disabled:opacity-60">{codeChecking ? "…" : "Apply"}</button>
              </div>
            )}
          </div>

          <div className="bg-[#12263A] text-white p-6 mt-6">
            <div className="text-xs uppercase tracking-widest text-[#C79A3B]">Booking fee due now{express?" (incl. express fee)":""}</div>
            <div className="text-3xl font-black mt-1" data-testid="deposit-total">£{Math.round(depositTotal).toLocaleString("en-GB")}</div>
            <div className="text-xs text-white/70 mt-2">
              Secure card payment via Stripe · £{Math.round(bookingFee).toLocaleString("en-GB")} booking fee to reserve your date &amp; survey
              {express ? ` · £${expressFee.toLocaleString("en-GB")} express fee` : ""}
            </div>
            {depositBalance > 0 && (
              <div className="text-xs text-white/70 mt-2 border-t border-white/10 pt-2" data-testid="deposit-balance-note">
                Balance of your 10% deposit — <b>£{Math.round(depositBalance).toLocaleString("en-GB")}</b> — is due after your on-site survey is completed{appliedCode?` (after ${appliedCode.label})`:""}. You&rsquo;ll pay it in the client portal (card or bank transfer).
              </div>
            )}
          </div>

          <div className="border border-[#E5E7EB] bg-white p-4 mt-6 text-sm">
            <div className="font-bold mb-2">Booking fee &amp; cancellation &mdash; please read</div>
            <p className="text-[#6B7280] leading-relaxed text-xs mb-3">
              Your <b>£{Math.round(bookingFee).toLocaleString("en-GB")} booking fee is fully refundable if you cancel within 14 days of booking</b>. It comes straight off your 10% deposit &mdash; you&rsquo;ll pay the balance of £{Math.round(depositBalance).toLocaleString("en-GB")} once your on-site survey is completed and you&rsquo;re happy to proceed. Cancellations made <b>more than 14 days after booking</b> are subject to a <b>£499 administration fee</b> (i.e. the booking fee is retained), plus any third-party expenses or statutory fees already committed on your behalf (for example Building Control fees, ordered materials, structural calculations). If <b>express service</b> is selected, the booking fee and the £2,499 express fee become <b>non-refundable</b> on payment, as we begin commissioning your slot immediately.
            </p>
            <label className="flex items-start gap-2 text-xs">
              <input data-testid="bk-policy-ack" type="checkbox" checked={policyAck} onChange={e=>setPolicyAck(e.target.checked)} className="mt-0.5"/>
              <span>I have read and understood the booking fee &amp; cancellation policy above{express?", including that my booking fee and express fee are non-refundable":""}.</span>
            </label>
          </div>

          <div className="border border-[#E5E7EB] bg-white p-4 mt-6 text-sm" data-testid="bk-finish-note">
            <div className="font-bold mb-2">Your finish — what to expect</div>
            <p className="text-[#6B7280] leading-relaxed text-xs">
              We hand the room over fully built and finished in <b>trade-white emulsion to walls &amp; ceiling only</b> — this is a clean, neutral base, not a decorative colour finish. <b>White switches and sockets</b>, <b>skirting boards</b> and <b>door/window architraves</b> are all included. Flooring is <b>fitting labour only</b> — you supply the flooring material itself. As a new extension settles, small <b>natural shrinkage/settlement cracks</b> can appear in the first year; we recommend booking your <b>proper decoration around 12 months after completion</b>, once any such cracks have shown and been made good, for the best long-term finish.
            </p>
          </div>

          <div className="mt-6 flex gap-3">
            <button data-testid="bk-back" className="de-btn-secondary" onClick={onBack}><ArrowLeft size={16}/> Back</button>
            <button data-testid="bk-submit" disabled={submitting} className="de-btn-primary" onClick={submit}>{submitting?"…":(express?"Pay & request express start":"Pay £499 & lock the slot")} <ArrowRight size={16}/></button>
          </div>
        </div>
      </div>
    </Section>
  );
}
